import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  RouterModule,
  RouterOutlet,
  withRouterConfig,
} from '@angular/router';
import { routes } from './app.routes';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { provideToastr } from 'ngx-toastr';
import { TOKEN_GETTER } from './core/utils/token-getter';
import { provideNgxMask } from 'ngx-mask';
import { BrowserModule } from '@angular/platform-browser';
import {
  EnvironmentService,
  apiConfigInitializer,
  appInitializer,
} from '@core/services';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ApiModule, BASE_PATH } from './client';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { JwtModule } from '@auth0/angular-jwt';
import {
  FORMLY_MODULE_CONFIG,
  FormlyGroupedSelectComponent,
  FormlyMaskedComponent,
  FormlyTreeSelectComponent,
} from '@shared';
import { FormlyModule } from '@ngx-formly/core';
import { withFormlyPrimeNG } from 'src/app/shared/formly-primeng';
import {
  ClientManagementService,
  LocationService,
} from '@elesgergo/ikont-web-client-sdk';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FormlyFieldCheckbox,
  FormlyFieldTextArea,
  FormlyWrapperFormField,
  withFormlyCheckbox,
  withFormlyInput,
  withFormlyTextArea,
} from './shared/formly-primeng/public_api';
import { Dialog } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { FormlyProductAutoCompleteComponent } from './feature/customer/components/customer-account-details/hardware-item-dialog/formly-product-autocomplete';
import { FormlyStorageSelectComponent } from './feature/customer/components/customer-account-details/hardware-item-dialog/formly-storage-select';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FormlyAutoCompleteComponent } from '@shared';
import ikontTheme from './core/themes/ikont-theme';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { UploadService } from './shared/service/upload.service';
import { FormlyButtonComponent } from './shared/formly-helper/custom-fields/formly-button/formly-button.component';
import { FormlyMultiselectComponent } from './shared/formly-helper/custom-fields/formly-multiselect/formly-multiselect.component';
import { FormlyCalendarComponent } from './shared/formly-helper/custom-fields/formly-calendar/formly-calendar.component';
import { FormlyDocumentUploadComponent } from './shared/formly-helper/custom-fields/formly-document-upload/formly-document-upload.component';
import { SectionWrapperComponent } from './shared/formly-helper/wrappers/section-wrapper/section-wrapper.component';
import { FormlyFieldInputNumber } from './shared/formly-helper/custom-fields/formly-inputnumber/formly-inputnumber.component.';
import { FormlyPayrollAutoCompleteComponent } from './feature/customer/components/payroll-new/formly-payroll-autocomplete';
import { FormlyToolAutoCompleteComponent } from './feature/customer/components/project-details/project-details-tools/formly-tool-autocomplete';
import { FormlyProjectAutoCompleteComponent } from './feature/customer/components/payroll-settlement-details/formly-project-autocomplete';
import { FormlyAdvanceAutoCompleteComponent } from './feature/customer/components/payroll-settlement-details/formly-advance-autocomplete';
import { APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { FormlyClientAutoCompleteComponent } from './feature/customer/components/customer-account-details/formly-client-autocomplete';
import { FormlyProjectWorkerAutoCompleteComponent } from './feature/customer/components/project-details/project-details-worker/formly-project-worker-autocomplete';

export const appConfig: ApplicationConfig = {
  providers: [
    FormlyModule.forRoot(withFormlyPrimeNG()).providers || [],
    {
      provide: 'FORMLY_CONFIG',
      useValue: FORMLY_MODULE_CONFIG,
      multi: true,
    },
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: ikontTheme,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'app-styles, sass-styles, primeng',
          },
        },
      },
      ripple: true,
    }),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      NgxSpinnerModule.forRoot(),
      NgxDropzoneModule,
      ApiModule,
      JwtModule.forRoot({
        config: {
          tokenGetter: TOKEN_GETTER,
          allowedDomains: ['localhost:3000', 'ikont-web-feature', 'ikont-web'],
        },
      }),
      FormlyModule.forRoot(FORMLY_MODULE_CONFIG),
      ReactiveFormsModule,
      FormlyModule.forRoot({
        types: [
          ...(withFormlyInput().types || []),
          ...(withFormlyCheckbox().types || []),
          ...(withFormlyTextArea().types || []),
          {
            name: 'product-autocomplete',
            component: FormlyProductAutoCompleteComponent,
          },
          { name: 'storage-select', component: FormlyStorageSelectComponent },
          { name: 'masked', component: FormlyMaskedComponent },
          { name: 'autocomplete', component: FormlyAutoCompleteComponent },
          { name: 'button', component: FormlyButtonComponent },
          { name: 'grouped-select', component: FormlyGroupedSelectComponent },
          { name: 'tree-select', component: FormlyTreeSelectComponent },
          { name: 'multiselect', component: FormlyMultiselectComponent },
          { name: 'datepicker', component: FormlyCalendarComponent },
          { name: 'document-upload', component: FormlyDocumentUploadComponent },
          //{ name: 'textarea', component: FormlyFieldTextArea },
          { name: 'inputnumber', component: FormlyFieldInputNumber },
          {
            name: 'currency',
            component: FormlyFieldInputNumber,
            defaultOptions: {
              templateOptions: {
                mode: 'currency',
                currency: 'HUF',
                locale: 'hu-HU',
              },
            },
          },
          {
            name: 'percentage',
            component: FormlyFieldInputNumber,
            defaultOptions: {
              templateOptions: {
                mode: 'decimal',
                suffix: '%',
                min: 0,
                max: 100,
              },
            },
          },
          {
            name: 'payroll-autocomplete',
            component: FormlyPayrollAutoCompleteComponent,
          },
          {
            name: 'tool-autocomplete',
            component: FormlyToolAutoCompleteComponent,
          },
          {
            name: 'project-autocomplete',
            component: FormlyProjectAutoCompleteComponent,
          },
          {
            name: 'advance-autocomplete',
            component: FormlyAdvanceAutoCompleteComponent,
          },
          {
            name: 'client-autocomplete',
            component: FormlyClientAutoCompleteComponent,
          },
          {
            name: 'project-worker-autocomplete',
            component: FormlyProjectWorkerAutoCompleteComponent,
          },
        ],
        validators: [
          {
            name: 'email',
            validation: (control) => {
              if (
                control.value &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(control.value)
              ) {
                return { email: true };
              }
              return null;
            },
          },
          { name: 'required', validation: Validators.required },
          {
            name: 'futureDateCheck',
            validation: (control) => {
              if (control.value && control.value instanceof Date) {
                const today = new Date();
                return control.value < today ? null : { futureDateCheck: true };
              }
              return null;
            },
          },
        ],
        validationMessages: [
          { name: 'email', message: 'Nem megfelelő email formátum!' },
          { name: 'required', message: 'Kötelező mező!' },
          { name: 'maxChar', message: 'A maximum karakter limit túllépve!' },
          { name: 'futureDateCheck', message: 'A dátum nem lehet a jövőben!' },
        ],
        wrappers: [
          {
            name: 'form-field',
            component: FormlyWrapperFormField,
          },
        ],
      })
    ),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-bottom-right',
      maxOpened: 3,
      autoDismiss: true,
      progressBar: true,
    }),
    provideNgxMask(),
    MessageService,
    EnvironmentService,
    ClientManagementService,
    LocationService,
    ConfirmationService,
    NgxSpinnerService,
    DialogService,
    ButtonModule,
    UploadService,
    provideAppInitializer(() => {
      const initializerFn = appInitializer(inject(EnvironmentService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: BASE_PATH,
      useFactory: apiConfigInitializer,
      deps: [EnvironmentService],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
